/**
 * Hooks de datos de administración.
 *
 * Módulo aparte de `api/hooks.ts` y no un apéndice suyo: `hooks.ts` es el borde
 * de las pantallas que usa cualquiera —catálogo, carrito, órdenes— y este es el
 * de las que solo abre el equipo. Separarlos deja ver de un vistazo qué
 * superficie de la API toca el panel interno, que es justamente la que tiene una
 * frontera de permisos adentro.
 *
 * Se conserva EL PATRÓN de `hooks.ts`: una clave de caché por recurso declarada
 * en un solo lugar (`clavesAdmin`), las invalidaciones apuntando a esas claves y
 * no a literales sueltos, y los tipos de entrada derivados de
 * `@compranorte/contracts` en vez de redeclarados —si mañana el esquema de
 * creación de proveedor pide algo más, esto deja de compilar—.
 *
 * Los tipos de SALIDA sí se declaran acá, como en `hooks.ts`: las proyecciones
 * de `modulos/admin.ts` se arman campo por campo según quién pregunta y no
 * existen como esquema en `contracts`. Lo que sí viene de `contracts` es cada
 * enumeración y cada tipo de métrica (`MetricasMes`, `ParametrosDto`,
 * `TramoTarifaDto`), de modo que un renombre allá rompe acá.
 */
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  InfiniteData,
  UseInfiniteQueryResult,
  UseMutationResult,
  UseQueryResult,
} from '@tanstack/react-query'
import type {
  CiudadOrigenDto,
  EntradaCrearCliente,
  EntradaCrearFaena,
  EntradaCrearProveedor,
  EntradaEditarCategoria,
  EntradaEditarCliente,
  EntradaEditarFaena,
  EntradaEditarParametros,
  EntradaEditarProveedor,
  EntradaEditarSolicitud,
  EntradaEditarTarifa,
  EntradaMarcarPago,
  EstadoProveedorDto,
  EstadoSolicitudDto,
  MetricasMes,
  ParametrosDto,
  Rol,
  TipoClienteDto,
  TramoTarifaDto,
  ZonaFleteDto,
  esquemaAsignarAcceso,
  EstadoLiquidacionDto,
  LineaLiquidableDto,
  LiquidacionDto,
  EntradaCambiarLiquidacion,
  EntradaAsignarOperadores,
  EntradaCrearOperador,
  EntradaEditarOperador,
  OperadorDto,
} from '@compranorte/contracts'
import type { EstadoOrdenDto } from '../lib/estados'
import { ErrorApi, pedir } from './cliente'
import { claves, type Pagina, type RespuestaMensaje } from './hooks'

/*
 * `contracts` no publica un alias para la entrada de asignación de acceso, así
 * que se deriva del esquema en vez de redeclararse a mano: si mañana el esquema
 * gana un campo, esto lo gana con él.
 */
type EntradaAsignarAcceso = ReturnType<typeof esquemaAsignarAcceso.parse>

/* ═══════════════════════════════════════════════════════════════════════════
 * Formas de salida
 * ═══════════════════════════════════════════════════════════════════════════ */

/** Ficha de proveedor tal como la proyecta `modulos/admin.ts`. */
export interface ProveedorAdmin {
  id: string
  razonSocial: string
  rut: string
  ciudadOrigen: CiudadOrigenDto
  estado: EstadoProveedorDto
  contacto: string | null
  email: string | null
  diasPago: number
  aprobadoEn: string | null
  aprobadoPor: string | null
  creadoEn: string
  /** Solo viene en el listado: cuántos productos tiene cargados. */
  productos?: number
}

export interface ClienteAdmin {
  id: string
  razonSocial: string
  rut: string
  tipo: TipoClienteDto
  diasPago: number
  contacto: string | null
  email: string | null
  activo: boolean
  /** Solo en el listado. */
  faenas?: number
  ordenes?: number
  creadoEn?: string
}

export interface FaenaAdmin {
  id: string
  clienteId: string
  clienteRazonSocial: string | null
  nombre: string
  ciudad: string
  region: string
  zonaFlete: ZonaFleteDto
  activa: boolean
}

/**
 * Lo que devuelve `PATCH /api/admin/categorias/:id`.
 *
 * Es el ÚNICO lugar de la API donde `margenDefault` se lee: ninguna consulta lo
 * publica —`GET /api/categorias` lo omite a propósito, porque con el margen y el
 * precio de venta se despeja el costo del proveedor, que era la fuga de la v1—.
 * De ahí que la pantalla de márgenes muestre el valor solo después de fijarlo.
 */
export interface CategoriaEditada {
  id: string
  slug: string
  nombre: string
  margenDefault: number
}

export interface TarifaRuta {
  id: string
  origen: CiudadOrigenDto
  zonaDestino: ZonaFleteDto
  /** Precio por kilo por encima del último tramo. */
  sobreKg: number
  pallet: number
  tramos: TramoTarifaDto[]
}

export interface PerfilAdmin {
  id: string
  nombre: string
  email: string
  rol: Rol
  activo: boolean
  /** Sin esto no se le puede asignar rol: la API responde 409. */
  correoVerificado: boolean
  proveedorId: string | null
  clienteId: string | null
  organizacion: string | null
  creadoEn: string
}

/** Acuse de `PATCH /api/admin/ordenes/:id/pago`. */
export interface PagoOrden {
  id: string
  numero: string
  pagadaEn: string | null
  diasCobro: number | null
}

/**
 * Resumen mensual de KPI.
 *
 * `truncado` viaja en la respuesta y la pantalla LO DICE. El defecto de la v1 no
 * fue tener un tope de órdenes por resumen, fue que el tope actuara en silencio:
 * el reporte recibía un subconjunto y publicaba un porcentaje que se veía
 * plausible y estaba mal.
 */
export interface ResumenMensual {
  datos: MetricasMes[]
  truncado: boolean
  desde: string
  hasta: string
}

/* ═══════════════════════════════════════════════════════════════════════════
 * Claves de caché
 * ═══════════════════════════════════════════════════════════════════════════ */

/** Filtros del listado de proveedores. Entran EN la clave, como en el catálogo. */
export interface FiltrosProveedores {
  estado?: EstadoProveedorDto
  q?: string
}

export interface FiltrosClientes {
  activo?: boolean
  q?: string
}

export const clavesAdmin = {
  proveedores: (f: FiltrosProveedores) => ['admin', 'proveedores', f] as const,
  clientes: (f: FiltrosClientes) => ['admin', 'clientes', f] as const,
  faenas: (clienteId?: string | null) => ['admin', 'faenas', clienteId ?? null] as const,
  parametros: ['admin', 'parametros'] as const,
  tarifas: ['admin', 'tarifas'] as const,
  perfiles: ['admin', 'perfiles'] as const,
  /* Los filtros entran EN la clave, como en el resto de los listados. */
  solicitudes: (f: FiltrosSolicitudes) => ['admin', 'solicitudes', f] as const,
  frecuentes: (p: { minimo?: number; estado?: string }) =>
    ['admin', 'frecuentes', p] as const,
  kpiMensual: (meses: number) => ['kpi', 'mensual', meses] as const,
  operadores: ['admin', 'operadores'] as const,
  /** Los operadores de UN cliente: la asignación se edita por cliente. */
  operadoresDeCliente: (clienteId: string) => ['admin', 'operadores', clienteId] as const,
}

const PAGINA = 50

function cadenaConsulta(
  partes: Record<string, string | number | boolean | null | undefined>,
): string {
  const p = new URLSearchParams()
  for (const [clave, valor] of Object.entries(partes)) {
    if (valor === undefined || valor === null || valor === '') continue
    p.set(clave, String(valor))
  }
  const s = p.toString()
  return s ? `?${s}` : ''
}

/**
 * Consulta paginada por cursor, que es la forma de TODO listado de la API.
 *
 * Se factoriza acá porque los seis listados de administración se paginan igual y
 * repetir el genérico de `useInfiniteQuery` seis veces es la clase de copia en la
 * que uno de los seis se queda sin `getNextPageParam` y trunca en silencio.
 */
function useListado<T>(
  clave: readonly unknown[],
  ruta: (cursor: string | null) => string,
  habilitada = true,
): UseInfiniteQueryResult<InfiniteData<Pagina<T>>, ErrorApi> {
  return useInfiniteQuery<
    Pagina<T>,
    ErrorApi,
    InfiniteData<Pagina<T>>,
    readonly unknown[],
    string | null
  >({
    queryKey: clave,
    queryFn: ({ pageParam }) => pedir<Pagina<T>>(ruta(pageParam)),
    initialPageParam: null,
    getNextPageParam: (ultima) => ultima.siguienteCursor,
    enabled: habilitada,
  })
}

/* ═══════════════════════════════════════════════════════════════════════════
 * Proveedores
 * ═══════════════════════════════════════════════════════════════════════════ */

export function useProveedoresAdmin(
  filtros: FiltrosProveedores = {},
  habilitada = true,
): UseInfiniteQueryResult<InfiniteData<Pagina<ProveedorAdmin>>, ErrorApi> {
  return useListado<ProveedorAdmin>(
    clavesAdmin.proveedores(filtros),
    (cursor) =>
      `/api/admin/proveedores${cadenaConsulta({
        cursor,
        limit: PAGINA,
        estado: filtros.estado,
        q: filtros.q,
      })}`,
    habilitada,
  )
}

export function useCrearProveedor(): UseMutationResult<
  ProveedorAdmin,
  ErrorApi,
  EntradaCrearProveedor
> {
  const qc = useQueryClient()
  return useMutation<ProveedorAdmin, ErrorApi, EntradaCrearProveedor>({
    mutationFn: (datos) =>
      pedir<ProveedorAdmin>('/api/admin/proveedores', { metodo: 'POST', cuerpo: datos }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'proveedores'] })
    },
  })
}

/**
 * Edición de proveedor.
 *
 * `estado` viaja por acá, pero el backend lo rechaza con 403 si quien pide no es
 * admin: la interfaz esconde ese control para un operador, que es una cortesía y
 * no un control de acceso.
 *
 * Aprobar o rechazar cambia qué productos son visibles, así que invalida también
 * el catálogo y las categorías —su contador cuenta solo lo visible—.
 */
export function useEditarProveedor(): UseMutationResult<
  ProveedorAdmin,
  ErrorApi,
  { id: string; cambios: EntradaEditarProveedor }
> {
  const qc = useQueryClient()
  return useMutation<ProveedorAdmin, ErrorApi, { id: string; cambios: EntradaEditarProveedor }>({
    mutationFn: ({ id, cambios }) =>
      pedir<ProveedorAdmin>(`/api/admin/proveedores/${encodeURIComponent(id)}`, {
        metodo: 'PATCH',
        cuerpo: cambios,
      }),
    onSuccess: (_p, { cambios }) => {
      void qc.invalidateQueries({ queryKey: ['admin', 'proveedores'] })
      if (cambios.estado !== undefined) {
        void qc.invalidateQueries({ queryKey: ['catalogo'] })
        void qc.invalidateQueries({ queryKey: claves.categorias })
      }
    },
  })
}

/* ═══════════════════════════════════════════════════════════════════════════
 * Clientes y faenas
 * ═══════════════════════════════════════════════════════════════════════════ */

export function useClientesAdmin(
  filtros: FiltrosClientes = {},
  habilitada = true,
): UseInfiniteQueryResult<InfiniteData<Pagina<ClienteAdmin>>, ErrorApi> {
  return useListado<ClienteAdmin>(
    clavesAdmin.clientes(filtros),
    (cursor) =>
      `/api/admin/clientes${cadenaConsulta({
        cursor,
        limit: PAGINA,
        activo: filtros.activo,
        q: filtros.q,
      })}`,
    habilitada,
  )
}

export function useCrearCliente(): UseMutationResult<ClienteAdmin, ErrorApi, EntradaCrearCliente> {
  const qc = useQueryClient()
  return useMutation<ClienteAdmin, ErrorApi, EntradaCrearCliente>({
    mutationFn: (datos) =>
      pedir<ClienteAdmin>('/api/admin/clientes', { metodo: 'POST', cuerpo: datos }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'clientes'] })
    },
  })
}

export function useEditarCliente(): UseMutationResult<
  ClienteAdmin,
  ErrorApi,
  { id: string; cambios: EntradaEditarCliente }
> {
  const qc = useQueryClient()
  return useMutation<ClienteAdmin, ErrorApi, { id: string; cambios: EntradaEditarCliente }>({
    mutationFn: ({ id, cambios }) =>
      pedir<ClienteAdmin>(`/api/admin/clientes/${encodeURIComponent(id)}`, {
        metodo: 'PATCH',
        cuerpo: cambios,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'clientes'] })
    },
  })
}

export function useFaenasAdmin(
  clienteId?: string | null,
  habilitada = true,
): UseInfiniteQueryResult<InfiniteData<Pagina<FaenaAdmin>>, ErrorApi> {
  return useListado<FaenaAdmin>(
    clavesAdmin.faenas(clienteId),
    (cursor) =>
      `/api/admin/faenas${cadenaConsulta({ cursor, limit: PAGINA, clienteId })}`,
    habilitada,
  )
}

export function useCrearFaena(): UseMutationResult<FaenaAdmin, ErrorApi, EntradaCrearFaena> {
  const qc = useQueryClient()
  return useMutation<FaenaAdmin, ErrorApi, EntradaCrearFaena>({
    mutationFn: (datos) => pedir<FaenaAdmin>('/api/admin/faenas', { metodo: 'POST', cuerpo: datos }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'faenas'] })
      // El contador de faenas del cliente queda viejo.
      void qc.invalidateQueries({ queryKey: ['admin', 'clientes'] })
    },
  })
}

export function useEditarFaena(): UseMutationResult<
  FaenaAdmin,
  ErrorApi,
  { id: string; cambios: EntradaEditarFaena }
> {
  const qc = useQueryClient()
  return useMutation<FaenaAdmin, ErrorApi, { id: string; cambios: EntradaEditarFaena }>({
    mutationFn: ({ id, cambios }) =>
      pedir<FaenaAdmin>(`/api/admin/faenas/${encodeURIComponent(id)}`, {
        metodo: 'PATCH',
        cuerpo: cambios,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'faenas'] })
    },
  })
}

/* ═══════════════════════════════════════════════════════════════════════════
 * Categorías
 * ═══════════════════════════════════════════════════════════════════════════ */

/**
 * Edita una categoría. `margenDefault` es de admin y el backend lo comprueba.
 *
 * Invalida el catálogo entero: el margen multiplica el precio de venta de todo
 * lo que cuelga de la categoría, así que cualquier página de catálogo que
 * quedara en caché está mostrando precios viejos.
 */
export function useEditarCategoria(): UseMutationResult<
  CategoriaEditada,
  ErrorApi,
  { id: string; cambios: EntradaEditarCategoria }
> {
  const qc = useQueryClient()
  return useMutation<CategoriaEditada, ErrorApi, { id: string; cambios: EntradaEditarCategoria }>({
    mutationFn: ({ id, cambios }) =>
      pedir<CategoriaEditada>(`/api/admin/categorias/${encodeURIComponent(id)}`, {
        metodo: 'PATCH',
        cuerpo: cambios,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: claves.categorias })
      void qc.invalidateQueries({ queryKey: ['catalogo'] })
      void qc.invalidateQueries({ queryKey: ['producto'] })
    },
  })
}

/* ═══════════════════════════════════════════════════════════════════════════
 * Parámetros y tarifas
 * ═══════════════════════════════════════════════════════════════════════════ */

/**
 * Los parámetros son de admin TAMBIÉN para leer: de `margenFletePct` y
 * `factoringMensualPct` se despeja el margen real de cualquier orden. Por eso el
 * hook recibe `habilitada` y la pantalla la apaga para un operador, en vez de
 * disparar una petición que va a volver 403.
 */
export function useParametros(habilitada = true): UseQueryResult<ParametrosDto, ErrorApi> {
  return useQuery<ParametrosDto, ErrorApi>({
    queryKey: clavesAdmin.parametros,
    queryFn: () => pedir<ParametrosDto>('/api/admin/parametros'),
    enabled: habilitada,
    retry: false,
  })
}

/**
 * Guarda los parámetros. Manda SOLO lo que cambió: el esquema exige al menos un
 * campo y un PATCH con los once valores repetidos escribiría una fila de
 * auditoría sin diferencias.
 *
 * Invalida catálogo y cotización: el IVA, el factor de flete y el ticket mínimo
 * rehacen el cálculo de cualquier cotización futura.
 */
export function useEditarParametros(): UseMutationResult<
  ParametrosDto,
  ErrorApi,
  EntradaEditarParametros
> {
  const qc = useQueryClient()
  return useMutation<ParametrosDto, ErrorApi, EntradaEditarParametros>({
    mutationFn: (cambios) =>
      pedir<ParametrosDto>('/api/admin/parametros', { metodo: 'PATCH', cuerpo: cambios }),
    onSuccess: (p) => {
      qc.setQueryData(clavesAdmin.parametros, p)
      void qc.invalidateQueries({ queryKey: ['catalogo'] })
      void qc.invalidateQueries({ queryKey: ['cotizacion'] })
    },
  })
}

export function useTarifas(habilitada = true): UseQueryResult<Pagina<TarifaRuta>, ErrorApi> {
  return useQuery<Pagina<TarifaRuta>, ErrorApi>({
    queryKey: clavesAdmin.tarifas,
    // Sin paginación, y es la única excepción del proyecto: la tabla es el
    // producto de cinco ciudades por cuatro zonas y editarla exige verla entera.
    queryFn: () => pedir<Pagina<TarifaRuta>>('/api/admin/tarifas'),
    enabled: habilitada,
    retry: false,
  })
}

export function useEditarTarifa(): UseMutationResult<
  TarifaRuta,
  ErrorApi,
  { rutaId: string; cambios: EntradaEditarTarifa }
> {
  const qc = useQueryClient()
  return useMutation<TarifaRuta, ErrorApi, { rutaId: string; cambios: EntradaEditarTarifa }>({
    mutationFn: ({ rutaId, cambios }) =>
      pedir<TarifaRuta>(`/api/admin/tarifas/${encodeURIComponent(rutaId)}`, {
        metodo: 'PATCH',
        cuerpo: cambios,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: clavesAdmin.tarifas })
      void qc.invalidateQueries({ queryKey: ['cotizacion'] })
    },
  })
}

/* ═══════════════════════════════════════════════════════════════════════════
 * KPI
 * ═══════════════════════════════════════════════════════════════════════════ */

/**
 * Resumen mensual. Del mes más reciente al más antiguo, con los meses vacíos
 * incluidos: un mes sin órdenes aparece con todas sus métricas en `null`, y ese
 * hueco es precisamente la información.
 *
 * `GET /api/kpi/ordenes` —la fila por orden con la que se audita un promedio— no
 * tiene hook acá porque ninguna de las cinco pestañas lo muestra todavía; se
 * agrega el día que exista la pantalla que lo use, y no antes.
 */
export function useKpiMensual(meses = 6): UseQueryResult<ResumenMensual, ErrorApi> {
  return useQuery<ResumenMensual, ErrorApi>({
    queryKey: clavesAdmin.kpiMensual(meses),
    queryFn: () => pedir<ResumenMensual>(`/api/kpi/mensual${cadenaConsulta({ meses })}`),
    // Agrega en memoria del servidor sobre las órdenes del rango: no es una
    // consulta para repetir cada vez que alguien vuelve a la pestaña.
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  })
}

/* ═══════════════════════════════════════════════════════════════════════════
 * Órdenes: estado y pago
 * ═══════════════════════════════════════════════════════════════════════════ */

/**
 * Avanza el estado de una orden.
 *
 * La API rechaza los SALTOS: de `recibida` solo se va a `confirmada`. La
 * pantalla ofrece únicamente el siguiente estado válido, así que el 409 no
 * debería ocurrir; si ocurre —porque otra persona movió la orden entretanto— el
 * mensaje del backend se muestra tal cual.
 *
 * Cancelar NO pasa por acá: exige motivo y tiene su propio endpoint
 * (`useCancelarOrden` en `hooks.ts`).
 */
export function useAvanzarEstado(): UseMutationResult<
  { estado: EstadoOrdenDto },
  ErrorApi,
  { id: string; estado: EstadoOrdenDto; nota?: string }
> {
  const qc = useQueryClient()
  return useMutation<
    { estado: EstadoOrdenDto },
    ErrorApi,
    { id: string; estado: EstadoOrdenDto; nota?: string }
  >({
    mutationFn: ({ id, estado, nota }) =>
      pedir<{ estado: EstadoOrdenDto }>(`/api/ordenes/${encodeURIComponent(id)}/estado`, {
        metodo: 'PATCH',
        cuerpo: nota ? { estado, nota } : { estado },
      }),
    onSuccess: (_r, { id }) => {
      void qc.invalidateQueries({ queryKey: claves.orden(id) })
      // Prefijo a propósito: hay una consulta por filtro de estado y todas
      // quedan viejas. Es el mismo criterio que usa `useCancelarOrden`.
      void qc.invalidateQueries({ queryKey: ['ordenes'] })
      // Entregar convierte la reserva en salida de stock.
      void qc.invalidateQueries({ queryKey: ['catalogo'] })
      void qc.invalidateQueries({ queryKey: ['kpi'] })
    },
  })
}

/**
 * Sella el pago de una orden.
 *
 * ESTE ENDPOINT ES LA RAZÓN DE QUE EL DSO PUEDA EXISTIR: en la v1 `pagadaEn`
 * estaba en el esquema y ningún camino de la aplicación la escribía, así que el
 * tablero mostraba el DSO en blanco para siempre y nadie sabía por qué.
 *
 * Cuerpo ausente significa "ahora"; `pagadaEn: null` deshace una imputación
 * errada.
 */
export function useMarcarPago(): UseMutationResult<
  PagoOrden,
  ErrorApi,
  { id: string; cuerpo?: EntradaMarcarPago }
> {
  const qc = useQueryClient()
  return useMutation<PagoOrden, ErrorApi, { id: string; cuerpo?: EntradaMarcarPago }>({
    mutationFn: ({ id, cuerpo }) =>
      pedir<PagoOrden>(`/api/admin/ordenes/${encodeURIComponent(id)}/pago`, {
        metodo: 'PATCH',
        cuerpo: cuerpo ?? {},
      }),
    onSuccess: (_r, { id }) => {
      void qc.invalidateQueries({ queryKey: claves.orden(id) })
      void qc.invalidateQueries({ queryKey: ['ordenes'] })
      // El DSO del mes cambia con cada pago sellado.
      void qc.invalidateQueries({ queryKey: ['kpi'] })
    },
  })
}

/* ═══════════════════════════════════════════════════════════════════════════
 * Cuentas
 * ═══════════════════════════════════════════════════════════════════════════ */

export function usePerfilesAdmin(
  habilitada = true,
): UseInfiniteQueryResult<InfiniteData<Pagina<PerfilAdmin>>, ErrorApi> {
  return useListado<PerfilAdmin>(
    clavesAdmin.perfiles,
    (cursor) => `/api/admin/perfiles${cadenaConsulta({ cursor, limit: PAGINA })}`,
    habilitada,
  )
}

/**
 * Asigna rol y organización, o suspende.
 *
 * Las dos columnas de organización se mandan SIEMPRE, y `null` cuando el rol no
 * la admite: el backend fusiona lo ausente con lo que ya había, así que omitir
 * `clienteId` al pasar de `cliente` a `operador` dejaría el viejo puesto y la
 * comprobación de coherencia devolvería 400.
 *
 * Invalida el perfil propio además de la lista: un admin puede estar
 * cambiándose a sí mismo, y su encabezado tiene que reflejarlo.
 */
export function useAsignarAcceso(): UseMutationResult<
  PerfilAdmin,
  ErrorApi,
  { id: string; cambios: EntradaAsignarAcceso }
> {
  const qc = useQueryClient()
  return useMutation<PerfilAdmin, ErrorApi, { id: string; cambios: EntradaAsignarAcceso }>({
    mutationFn: ({ id, cambios }) =>
      pedir<PerfilAdmin>(`/api/admin/perfiles/${encodeURIComponent(id)}`, {
        metodo: 'PATCH',
        cuerpo: cambios,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: clavesAdmin.perfiles })
      void qc.invalidateQueries({ queryKey: claves.yo })
    },
  })
}

/**
 * Envía un enlace de restablecimiento.
 *
 * El admin **nunca ve ni fija** la contraseña: la respuesta es un mensaje, no
 * una clave. No debe poder entrar como otra persona.
 */
export function useResetClave(): UseMutationResult<RespuestaMensaje, ErrorApi, { id: string }> {
  return useMutation<RespuestaMensaje, ErrorApi, { id: string }>({
    mutationFn: ({ id }) =>
      pedir<RespuestaMensaje>(`/api/admin/perfiles/${encodeURIComponent(id)}/reset-clave`, {
        metodo: 'POST',
      }),
  })
}

/* ═══════════════════════════════════════════════════════════════════════════
 * Demanda no cubierta
 * ═══════════════════════════════════════════════════════════════════════════ */

/**
 * Una solicitud tal como la ve el EQUIPO: con nota interna y con cliente.
 *
 * Es una forma distinta de `SolicitudPropia` en `hooks.ts` y no un supertipo con
 * campos opcionales, a propósito: los tres campos de más son justamente los que
 * el servidor omite para un cliente, y declararlos opcionales en un solo tipo
 * dejaría que una pantalla de cliente los pintara «si vienen».
 */
export interface SolicitudAdmin {
  id: string
  busqueda: string
  detalle: string | null
  cantidad: number | null
  estado: EstadoSolicitudDto
  faenaId: string | null
  faenaNombre: string | null
  creadoEn: string
  clienteId: string | null
  clienteRazonSocial: string | null
  notaInterna: string | null
  creadoPor: string | null
}

/** Una demanda repetida, ya contada y ordenada por el servidor. */
export interface DemandaFrecuente {
  /** Una variante REAL de lo que se escribió, no la clave normalizada. */
  texto: string
  solicitudes: number
  /** Clientes DISTINTOS que lo pidieron. */
  clientes: number
  cantidadTotal: number
  ultima: string
}

/**
 * El informe de frecuentes.
 *
 * `truncado` viaja en la respuesta y la pantalla LO DICE, igual que en el
 * resumen de KPI: el agrupamiento se hace sobre una muestra de las solicitudes
 * más recientes, y un informe que recorta en silencio publica una prioridad
 * calculada sobre un subconjunto.
 */
export interface InformeFrecuentes {
  datos: DemandaFrecuente[]
  /** Cuántos grupos cumplen el mínimo, aunque no quepan en el límite pedido. */
  grupos: number
  /** Sobre cuántas solicitudes se calculó. */
  revisadas: number
  truncado: boolean
}

export interface FiltrosSolicitudes {
  estado?: EstadoSolicitudDto
  clienteId?: string
  q?: string
}

export function useSolicitudesAdmin(
  filtros: FiltrosSolicitudes = {},
  habilitada = true,
): UseInfiniteQueryResult<InfiniteData<Pagina<SolicitudAdmin>>, ErrorApi> {
  return useListado<SolicitudAdmin>(
    clavesAdmin.solicitudes(filtros),
    (cursor) =>
      `/api/admin/solicitudes${cadenaConsulta({
        cursor,
        limit: PAGINA,
        estado: filtros.estado,
        clienteId: filtros.clienteId,
        q: filtros.q,
      })}`,
    habilitada,
  )
}

export function useSolicitudesFrecuentes(
  parametros: { minimo?: number; estado?: EstadoSolicitudDto | 'todos' } = {},
  habilitada = true,
): UseQueryResult<InformeFrecuentes, ErrorApi> {
  return useQuery<InformeFrecuentes, ErrorApi>({
    queryKey: clavesAdmin.frecuentes(parametros),
    queryFn: () =>
      pedir<InformeFrecuentes>(
        `/api/admin/solicitudes/frecuentes${cadenaConsulta({
          minimo: parametros.minimo,
          estado: parametros.estado,
        })}`,
      ),
    enabled: habilitada,
  })
}

/**
 * Cataloga o descarta una solicitud.
 *
 * Invalida el listado Y el informe: cerrar una solicitud la saca del informe
 * —que por defecto mira solo lo abierto—, y si no se invalidara, la fila
 * seguiría contando en la lista de prioridades después de resolverse.
 */
export function useEditarSolicitud(): UseMutationResult<
  SolicitudAdmin,
  ErrorApi,
  { id: string; cambios: EntradaEditarSolicitud }
> {
  const qc = useQueryClient()
  return useMutation<SolicitudAdmin, ErrorApi, { id: string; cambios: EntradaEditarSolicitud }>({
    mutationFn: ({ id, cambios }) =>
      pedir<SolicitudAdmin>(`/api/admin/solicitudes/${encodeURIComponent(id)}`, {
        metodo: 'PATCH',
        cuerpo: cambios,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'solicitudes'] })
      void qc.invalidateQueries({ queryKey: ['admin', 'frecuentes'] })
    },
  })
}

/* ═══════════════════════════════════════════════════════════════════════════
 * Liquidación a proveedores
 * ═══════════════════════════════════════════════════════════════════════════ */

/**
 * Las liquidaciones, con sus filtros.
 *
 * Sirve a dos pantallas con la misma consulta: la del EQUIPO —«qué pago esta
 * semana», con `venceEnDias`— y la del PROVEEDOR —«cuándo me pagan»—. El
 * servidor decide qué se ve según el alcance, así que el hook no necesita
 * distinguirlas: pedir la de otro devuelve lista vacía, no un error.
 */
export interface FiltrosLiquidaciones {
  estado?: EstadoLiquidacionDto
  proveedorId?: string
  venceEnDias?: number
}

export function useLiquidaciones(
  filtros: FiltrosLiquidaciones = {},
  habilitada = true,
): UseInfiniteQueryResult<InfiniteData<Pagina<LiquidacionDto>>, ErrorApi> {
  return useListado<LiquidacionDto>(
    ['liquidaciones', filtros],
    (cursor) =>
      `/api/liquidaciones${cadenaConsulta({
        cursor,
        limit: PAGINA,
        estado: filtros.estado,
        proveedorId: filtros.proveedorId,
        venceEnDias: filtros.venceEnDias,
      })}`,
    habilitada,
  )
}

/** Lo pendiente de liquidar de un proveedor: la pantalla previa a crear la liquidación. */
export function usePendienteDeLiquidar(
  proveedorId: string,
  habilitada = true,
): UseQueryResult<{ datos: LineaLiquidableDto[]; total: number }, ErrorApi> {
  return useQuery({
    queryKey: ['liquidaciones', 'pendiente', proveedorId],
    queryFn: () =>
      pedir<{ datos: LineaLiquidableDto[]; total: number }>(
        `/api/liquidaciones/pendiente?proveedorId=${encodeURIComponent(proveedorId)}`,
      ),
    enabled: habilitada && proveedorId !== '',
    staleTime: 15_000,
  })
}

export function useCrearLiquidacion(): UseMutationResult<
  { id: string; numero: string; total: number; lineas: number },
  ErrorApi,
  { proveedorId: string }
> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (cuerpo) =>
      pedir<{ id: string; numero: string; total: number; lineas: number }>('/api/liquidaciones', {
        metodo: 'POST',
        cuerpo,
      }),
    onSuccess: () => {
      // Y lo pendiente también: acaba de quedar vacío.
      void qc.invalidateQueries({ queryKey: ['liquidaciones'] })
    },
  })
}

export function useCambiarLiquidacion(): UseMutationResult<
  { estado: string; totalAPagar: number },
  ErrorApi,
  { id: string; cambios: EntradaCambiarLiquidacion }
> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, cambios }) =>
      pedir<{ estado: string; totalAPagar: number }>(
        `/api/liquidaciones/${encodeURIComponent(id)}`,
        { metodo: 'PATCH', cuerpo: cambios },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['liquidaciones'] })
    },
  })
}


/* ─── Operadores logísticos ───────────────────────────────────────────────────
 *
 * El operador es el ANDÉN DONDE ENTREGAMOS la carga: nuestro transporte llega a
 * él y la minera hace el último tramo hasta su faena. El cliente lo elige al
 * comprar, y estas pantallas son las que hacen que haya algo que elegir: las
 * tablas nacen vacías, así que sin el alta y la asignación el selector del
 * carrito quedaría en blanco para siempre.
 *
 * NO tarifican. La zona del operador se guarda porque conceptualmente él es el
 * destino del tramo que sí hacemos, pero el flete se sigue calculando contra la
 * zona de la FAENA hasta que exista el tarifario del contrato.
 * ─────────────────────────────────────────────────────────────────────────── */

/** Un operador, más si este cliente lo tiene habilitado. */
export interface OperadorDeCliente extends OperadorDto {
  asignado: boolean
  predeterminado: boolean
}

export function useOperadoresAdmin(): UseQueryResult<Pagina<OperadorDto>, ErrorApi> {
  return useQuery<Pagina<OperadorDto>, ErrorApi>({
    queryKey: clavesAdmin.operadores,
    queryFn: () =>
      pedir<Pagina<OperadorDto>>(`/api/admin/operadores${cadenaConsulta({ limit: 200 })}`),
    staleTime: 60_000,
  })
}

export function useCrearOperador(): UseMutationResult<OperadorDto, ErrorApi, EntradaCrearOperador> {
  const qc = useQueryClient()
  return useMutation<OperadorDto, ErrorApi, EntradaCrearOperador>({
    mutationFn: (datos) =>
      pedir<OperadorDto>('/api/admin/operadores', { metodo: 'POST', cuerpo: datos }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: clavesAdmin.operadores })
    },
  })
}

export function useEditarOperador(): UseMutationResult<
  OperadorDto,
  ErrorApi,
  { id: string; cambios: EntradaEditarOperador }
> {
  const qc = useQueryClient()
  return useMutation<OperadorDto, ErrorApi, { id: string; cambios: EntradaEditarOperador }>({
    mutationFn: ({ id, cambios }) =>
      pedir<OperadorDto>(`/api/admin/operadores/${encodeURIComponent(id)}`, {
        metodo: 'PATCH',
        cuerpo: cambios,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: clavesAdmin.operadores })
      /*
       * Desactivar un operador lo saca de la lista de TODOS los clientes que lo
       * tenían, así que se invalida la asignación entera y no solo el listado.
       */
      void qc.invalidateQueries({ queryKey: ['admin', 'operadores'] })
    },
  })
}

/**
 * Todos los operadores activos, marcados con si este cliente los tiene.
 *
 * Devuelve la lista COMPLETA y no solo los asignados: la pantalla es de
 * casillas, y pedir solo los asignados obligaría a cruzar dos consultas en el
 * navegador para dibujar las que faltan.
 */
export function useOperadoresDeCliente(
  clienteId: string,
): UseQueryResult<Pagina<OperadorDeCliente>, ErrorApi> {
  return useQuery<Pagina<OperadorDeCliente>, ErrorApi>({
    queryKey: clavesAdmin.operadoresDeCliente(clienteId),
    queryFn: () =>
      pedir<Pagina<OperadorDeCliente>>(
        `/api/admin/clientes/${encodeURIComponent(clienteId)}/operadores`,
      ),
    enabled: clienteId !== '',
  })
}

/**
 * Reemplaza la asignación completa de un cliente.
 *
 * Manda el conjunto ENTERO y no un alta o baja por operador: con casillas en
 * pantalla, ir de a una deja un estado intermedio donde una llamada ya quitó y
 * la otra todavía no agregó, y en una pantalla de permisos ese segundo es la
 * diferencia entre «sin operadores» y una orden rechazada.
 */
export function useAsignarOperadores(
  clienteId: string,
): UseMutationResult<unknown, ErrorApi, EntradaAsignarOperadores> {
  const qc = useQueryClient()
  return useMutation<unknown, ErrorApi, EntradaAsignarOperadores>({
    mutationFn: (datos) =>
      pedir(`/api/admin/clientes/${encodeURIComponent(clienteId)}/operadores`, {
        metodo: 'PUT',
        cuerpo: datos,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: clavesAdmin.operadoresDeCliente(clienteId) })
      void qc.invalidateQueries({ queryKey: clavesAdmin.operadores })
      // La lista que ve el cliente al comprar sale de otro endpoint.
      void qc.invalidateQueries({ queryKey: claves.operadores })
    },
  })
}
